import React from "react";
import axios from "axios";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Alert,
  Badge
} from "reactstrap";
import moment from "moment";
import { Enviroments } from "../../enviroments/enviroments";

/* ====== Tema ====== */
const COLORS = {
  bg: "#0B0B0F",
  surface: "#121317",
  border: "#26272B",
  text: "#ECECF3",
  sub: "#A3A6AF",
  primary: "#913ef8",
  pink: "#e14eca",
  yellow: "#F6E05E",
  low: "#8B8E98",
};

/* ====== Skeleton ====== */
const Shimmer = ({ width = "100%", height = 16, radius = 6, className = "" }) => (
  <div
    className={className}
    style={{
      width,
      height,
      borderRadius: radius,
      background:
        "linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 37%, rgba(255,255,255,0.06) 63%)",
      backgroundSize: "400% 100%",
      animation: "shimmer 1.2s ease-in-out infinite",
    }}
  />
);

const GlobalStyles = () => (
  <style>{`
    @keyframes shimmer {
      0% { background-position: 100% 0; }
      100% { background-position: -100% 0; }
    }
    .hour-row { display:flex; align-items:center; gap:10px; }
    .hour-bar {
      height: 10px;
      border-radius: 6px;
      background: linear-gradient(90deg, #3a3b41, #565962);
    }
  `}</style>
);

/* ====== Subcomponentes ====== */

function HoursBlock({ title, data, loading, accent = COLORS.primary }) {
  // data esperado: { oddsByHour: [{ hour: "00:00", count: 3 }, ...] }
  const list = data?.oddsByHour || [];
  const total = list.reduce((s, i) => s + Number(i.count || 0), 0);
  const max = Math.max(1, ...list.map((i) => Number(i.count || 0)));
  const best = list.reduce(
    (acc, cur) => (Number(cur.count) > Number(acc.count) ? cur : acc),
    { hour: "--:--", count: 0 }
  );

  return (
    <Card style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
      <CardHeader className="pb-2">
        <h6 className="card-category mb-1" style={{ color: COLORS.sub }}>
          Histórico de payout por horário
        </h6>
        <div className="d-flex align-items-center justify-content-between">
          <CardTitle tag="h4" className="mb-0" style={{ color: COLORS.text }}>
            {title}
          </CardTitle>
          {!loading && (
            <Badge pill style={{ background: "#1b1c21", border: `1px solid ${COLORS.border}`, color: COLORS.sub }}>
              Total: <span className="ml-1" style={{ color: COLORS.text, fontWeight: 700 }}>{total}</span>
            </Badge>
          )}
        </div>
        {!loading && (
          <div style={{ color: COLORS.sub, fontSize: 12, marginTop: 6 }}>
            Melhor horário: <strong style={{ color: accent }}>{best.hour}</strong> • {best.count} ocorrência(s)
          </div>
        )}
      </CardHeader>

      <CardBody className="p-3">
        {loading ? (
          <>
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div className="d-flex justify-content-between" style={{ color: COLORS.sub, fontSize: 12 }}>
                  <Shimmer width={70} height={12} />
                  <Shimmer width={24} height={12} />
                </div>
                <Shimmer height={10} />
              </div>
            ))}
          </>
        ) : list.length ? (
          list
            .slice() // cópia
            .sort((a, b) => a.hour.localeCompare(b.hour)) // 00:00 -> 23:59
            .map((item, idx) => {
              const count = Number(item.count || 0);
              const pct = Math.max(2, Math.round((count / max) * 100));
              const isBest = item.hour === best.hour && count === best.count;
              return (
                <div key={idx} style={{ marginBottom: 10 }}>
                  <div className="d-flex justify-content-between" style={{ color: COLORS.sub, fontSize: 12 }}>
                    <span style={{ color: isBest ? accent : COLORS.sub, fontWeight: isBest ? 700 : 400 }}>
                      {item.hour}
                    </span>
                    <span>{count}</span>
                  </div>
                  <div
                    className="hour-bar"
                    style={{
                      width: `${pct}%`,
                      background: `linear-gradient(90deg, ${accent}33, ${accent})`,
                    }}
                  />
                </div>
              );
            })
        ) : (
          <div style={{ color: COLORS.sub, fontSize: 13 }}>Sem dados para exibir.</div>
        )}
      </CardBody>
    </Card>
  );
}

/* ====== Componente Principal ====== */

function HistoryHours({ props }) {
  const dateNow = React.useMemo(() => moment(new Date()).format("YYYY-MM-DD"), []);
  const [data10, setData10] = React.useState(null);
  const [data30, setData30] = React.useState(null);
  const [data100, setData100] = React.useState(null);
  const [loading10, setLoading10] = React.useState(true);
  const [loading30, setLoading30] = React.useState(true);
  const [loading100, setLoading100] = React.useState(true);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [updatedAt, setUpdatedAt] = React.useState(null);

  const betHouseCustom = React.useMemo(
    () => (props?.betHouse === "BR4BET" ? "MINES_BET" : props?.betHouse),
    [props?.betHouse]
  );

  const getHistoryHourAviator = React.useCallback(async (odd) => {
    try {
      const headers = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("apiToken") || ""}`,
        },
      };
      const url = `${Enviroments.API_URL_NODE}candles-by-hour?date=${dateNow}&odd=${odd}&betHouse=${betHouseCustom}`;
      const { data } = await axios.get(url, headers);
      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }, [dateNow, betHouseCustom]);

  const fetchAll = React.useCallback(async () => {
    setErrorMsg("");
    setLoading10(true); setLoading30(true); setLoading100(true);
    try {
      const [r10, r30, r100] = await Promise.all([
        getHistoryHourAviator(10),
        getHistoryHourAviator(30),
        getHistoryHourAviator(100),
      ]);
      setData10(r10 || null);
      setData30(r30 || null);
      setData100(r100 || null);
      setUpdatedAt(moment().format("HH:mm:ss"));
    } catch (e) {
      setErrorMsg("Não foi possível carregar o histórico por horário agora. Tente novamente mais tarde.");
    } finally {
      setLoading10(false); setLoading30(false); setLoading100(false);
    }
  }, [getHistoryHourAviator]);

  React.useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return (
    <div className="history-hours" style={{ color: COLORS.text }}>
      <GlobalStyles />

      <Row className="align-items-center mb-2">
        <Col xs="12" md="6">
          <h4 className="m-0">Padrões por horário</h4>
          <div style={{ color: COLORS.sub, fontSize: 12 }}>
            Distribuição de ocorrências por hora (dia {moment(dateNow).format("DD/MM/YYYY")})
          </div>
        </Col>
        <Col xs="12" md="6" className="text-md-right mt-2 mt-md-0">
          <Button
            color="primary"
            onClick={fetchAll}
            style={{ background: COLORS.primary, border: "none", minWidth: 140 }}
            disabled={loading10 || loading30 || loading100}
          >
            {loading10 || loading30 || loading100 ? "Atualizando..." : "Atualizar"}
          </Button>
          {updatedAt && (
            <span className="ml-3" style={{ color: COLORS.sub, fontSize: 12 }}>
              Última atualização: <strong style={{ color: COLORS.text }}>{updatedAt}</strong>
            </span>
          )}
        </Col>
      </Row>

      {errorMsg && (
        <Row className="mb-2">
          <Col xs="12">
            <Alert color="danger" style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col md={4} className="mb-3">
          <HoursBlock
            title="Payout ≥ 10x"
            data={data10}
            loading={loading10}
            accent={COLORS.pink}
          />
        </Col>
        <Col md={4} className="mb-3">
          <HoursBlock
            title="Payout ≥ 30x"
            data={data30}
            loading={loading30}
            accent={COLORS.pink}
          />
        </Col>
        <Col md={4} className="mb-3">
          <HoursBlock
            title="Payout ≥ 100x"
            data={data100}
            loading={loading100}
            accent={COLORS.pink}
          />
        </Col>
      </Row>
    </div>
  );
}

export default HistoryHours;
